import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router-dom';
import { TEMPLATE_QUERY_PARAM, DEFAULT_TEMPLATE_PARAM_VALUE } from '../constants/routes';

const EMPTY_TEMPLATES = [];


export function useWorkkardTemplates({ isEdit = false } = {}) {
  const userTemplates = useSelector(
    (state) => state.sessionContextOidc?.userView?.templates ?? EMPTY_TEMPLATES
  );

  const templates = useMemo(
    () => userTemplates
      .filter((t) => t.module === 'workkard')
      .map((t) => ({
        id: t.id,
        name: t.name,
        template_short_name: t.short_name,
      })),
    [userTemplates],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const locationStateRef = useRef(location.state);
  locationStateRef.current = location.state;
  const templateParam = searchParams.get(TEMPLATE_QUERY_PARAM);

  const selectedTemplateShortName =
    templateParam && templateParam !== DEFAULT_TEMPLATE_PARAM_VALUE ? templateParam : null;

  const setSelectedTemplateShortName = useCallback((shortName) => {
    setSearchParams(
      (previousParams) => {
        const updatedParams = new URLSearchParams(previousParams);
        updatedParams.set(TEMPLATE_QUERY_PARAM, shortName || DEFAULT_TEMPLATE_PARAM_VALUE);
        return updatedParams;
      },
      { replace: true, state: locationStateRef.current },
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!isEdit && !templateParam) {
      setSelectedTemplateShortName(null);
    }
  }, [isEdit, templateParam, setSelectedTemplateShortName]);

  const selectedTemplateName = useMemo(() => {
    if (!selectedTemplateShortName) return null;
    const matching = templates.find((t) => t.template_short_name === selectedTemplateShortName);
    return matching?.name ?? null;
  }, [templates, selectedTemplateShortName]);

  return {
    templates,
    areTemplatesLoading: false,
    templatesError: null,
    selectedTemplateShortName,
    setSelectedTemplateShortName,
    selectedTemplateName,
  };
}
